<!-- Account Information — profile redesign (handoff panel 02).
     All logic is unchanged: reactive form + username async validator +
     confirmation dialog + updateUserInformation() save path + the phone
     verification hand-off. Only the presentation is new. -->

<!-- Saving state lives on the Save button (vb-button [loading]) — the form
     stays mounted during the request so the panel doesn't collapse. -->
<section class="vb-info" *ngIf="!verifyPhoneNumber">
    <vb-panel-header
        title="Contact Information"
        subtitle="Your contact information, as shown to Viewbid staff. Other users can't see this information.">
        <div panelActions>
            <vb-button
                *ngIf="!isEditMode && currentUser"
                variant="secondary"
                [iconLeft]="editIcon"
                (clicked)="toggleEditMode()">
                Edit profile
            </vb-button>
        </div>
    </vb-panel-header>
    <ng-template #editIcon>
        <vb-icon name="note" [size]="16"></vb-icon>
    </ng-template>

    <!-- Read-only card: 210px/1fr label-value rows, hairline dividers -->
    <div class="vb-info__card" *ngIf="!isEditMode && currentUser">
        <div class="vb-info__row">
            <span class="vb-info__label">Email</span>
            <span class="vb-info__value">
                {{ userEmail || currentUser.email }}
            </span>
        </div>
        <div class="vb-info__row">
            <span class="vb-info__label">Username</span>
            <span class="vb-info__value">{{ currentUser.username }}</span>
        </div>
        <div class="vb-info__row">
            <span class="vb-info__label">First name</span>
            <span class="vb-info__value">{{ currentUser.firstname }}</span>
        </div>
        <div class="vb-info__row">
            <span class="vb-info__label">Last name</span>
            <span class="vb-info__value">{{ currentUser.lastname }}</span>
        </div>
        <div class="vb-info__row">
            <span class="vb-info__label">Phone number</span>
            <span class="vb-info__value vb-info__value--mono">
                {{ currentUser.accountTelephone?.number }}
                <vb-button
                    *ngIf="!isTelephoneVerified"
                    variant="ghost"
                    size="sm"
                    (clicked)="onVerifyPhoneNumber()">
                    Verify
                </vb-button>
            </span>
        </div>
    </div>

    <!-- Edit mode: the existing reactive form, restyled with tokens -->
    <form
        class="vb-info__form"
        id="userInformationForm"
        *ngIf="isEditMode"
        [formGroup]="userInformationForm">
        <div class="vb-info__field">
            <label class="vb-info__field-label" for="email">Email</label>
            <input
                class="vb-info__input"
                name="email"
                id="email"
                type="text"
                placeholder="Enter email..."
                formControlName="email"
                readonly />
            <span class="vb-info__error" *ngIf="getHelperText('email')">
                {{ getHelperText("email") }}
            </span>
        </div>
        <div class="vb-info__field">
            <label class="vb-info__field-label" for="username">Username</label>
            <input
                class="vb-info__input"
                name="username"
                id="username"
                type="text"
                placeholder="Enter Username..."
                formControlName="username"
                readonly />
            <span class="vb-info__error" *ngIf="getHelperText('username')">
                {{ getHelperText("username") }}
            </span>
        </div>
        <div class="vb-info__field">
            <label class="vb-info__field-label" for="firstname">
                First name
            </label>
            <input
                class="vb-info__input"
                name="firstname"
                id="firstname"
                type="text"
                placeholder="Enter first name..."
                formControlName="firstname" />
            <span class="vb-info__error" *ngIf="getHelperText('firstname')">
                {{ getHelperText("firstname") }}
            </span>
        </div>
        <div class="vb-info__field">
            <label class="vb-info__field-label" for="lastname">
                Last name
            </label>
            <input
                class="vb-info__input"
                name="lastname"
                id="lastname"
                type="text"
                placeholder="Enter last name..."
                formControlName="lastname" />
            <span class="vb-info__error" *ngIf="getHelperText('lastname')">
                {{ getHelperText("lastname") }}
            </span>
        </div>
        <div class="vb-info__field">
            <label class="vb-info__field-label" for="phoneNumber">
                Phone number
            </label>
            <input
                class="vb-info__input vb-info__input--mono"
                name="phoneNumber"
                id="phoneNumber"
                type="tel"
                placeholder="XXX XXX XXXX"
                formControlName="phoneNumber"
                maxlength="12" />
            <span class="vb-info__error" *ngIf="getHelperText('phoneNumber')">
                {{ getHelperText("phoneNumber") }}
            </span>
        </div>
        <div class="vb-info__actions">
            <vb-button
                variant="secondary"
                [disabled]="isLoading"
                (clicked)="toggleEditMode()">
                Cancel
            </vb-button>
            <vb-button
                variant="primary"
                [loading]="isLoading"
                [disabled]="userInformationForm.invalid || isLoading"
                (clicked)="openConfirmationModal()">
                Save changes
            </vb-button>
        </div>
    </form>

    <!-- Save confirm — vb-dialog with the same translated copy the legacy
         ngx-bootstrap confirmation showed. -->
    <vb-dialog
        [open]="confirmOpen"
        [title]="confirmTitle"
        (closed)="confirmOpen = false">
        <p class="vb-info__confirm-copy">{{ confirmMessage }}</p>
        <!-- vb-dialog's footer slot stacks children (its full-width recipe);
             a single row wrapper keeps Cancel/Confirm side by side. -->
        <div class="vb-info__confirm-actions" dialogFooter>
            <vb-button
                variant="ghost"
                [fullWidth]="true"
                (clicked)="confirmOpen = false">
                Cancel
            </vb-button>
            <vb-button
                variant="primary"
                [fullWidth]="true"
                (clicked)="onSaveConfirmed()">
                Confirm
            </vb-button>
        </div>
    </vb-dialog>
</section>

<vb-verify-phone-number
    *ngIf="verifyPhoneNumber"
    (isPhoneNumberVerified)="onTelephoneVerified($event)"
    [isFromProfile]="true" />
